<script setup lang="ts">
import { BaseHeader, type BaseHeaderImage } from '@components/base';
import { computed, ref } from 'vue';

type FaqItem = {
  question: string;
  answer: string;
};

const props = withDefaults(
  defineProps<{
    headingHtml?: string;
    headingObjects?: BaseHeaderImage[] | string;
    items?: FaqItem[] | string;
    ctaHref?: string;
    ctaTarget?: string;
    ctaText?: string;
    ctaBg?: string | null;
  }>(),
  {
    headingHtml: '',
    headingObjects: () => [],
    items: () => [],
    ctaHref: '',
    ctaTarget: '_self',
    ctaText: '',
    ctaBg: '',
  },
);

function parseHeadingObjects(raw: BaseHeaderImage[] | string | undefined): BaseHeaderImage[] {
  if (raw === undefined || raw === null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    let s = raw.trim();
    if (s === '' || s === '[]') return [];
    try {
      return Array.isArray(JSON.parse(s)) ? (JSON.parse(s) as BaseHeaderImage[]) : [];
    } catch {
      if (s.includes('&quot;')) {
        s = s.replace(/&quot;/g, '"');
        try {
          return Array.isArray(JSON.parse(s)) ? (JSON.parse(s) as BaseHeaderImage[]) : [];
        } catch {
          return [];
        }
      }
      return [];
    }
  }
  return [];
}

function parseFaqItems(raw: FaqItem[] | string | undefined): FaqItem[] {
  if (raw === undefined || raw === null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (s === '' || s === '[]') return [];
    try {
      const parsed = JSON.parse(s) as unknown;
      return Array.isArray(parsed) ? (parsed as FaqItem[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

const headingObjectsList = computed(() => parseHeadingObjects(props.headingObjects));
const faqItems = computed(() => parseFaqItems(props.items));

/** Craft pricing table / CP HTML: preserve line breaks & tabs; allow safe-looking HTML when present (CP-trusted). */
function escapeHtmlPlain(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function faqAnswerHtml(raw: string): string {
  if (raw === undefined || raw === null || raw === '') {
    return '';
  }
  const s = String(raw).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const looksLikeMarkup = /<\/?[a-z][\s\S]*>/i.test(s);
  if (!looksLikeMarkup) {
    return escapeHtmlPlain(s).replace(/\n/g, '<br />').replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');
  }
  return s.replace(/\n/g, '<br />').replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');
}

const openIndex = ref<number | null>(null);

function toggle(index: number) {
  openIndex.value = openIndex.value === index ? null : index;
}

const headingContentClass = 'font-inter-tight text-32 font-semibold leading-[1.1] tracking-[-0.01em] text-off-primary-500 sm:text-38 md:text-44 lg:text-52 [&_p]:m-0 [&_p]:leading-[inherit]';

const ctaStyle = computed(() => {
  const bg = props.ctaBg?.trim();
  if (!bg) return undefined;
  return { backgroundColor: bg };
});

const show = computed(() => {
  const t = (props.headingHtml ?? '').replace(/<[^>]*>/g, '').trim();
  return t.length > 0 || faqItems.value.length > 0;
});
</script>

<template>
  <section
    v-if="show"
    class="pricing-faq lg:py-72 py-40 md:py-56"
  >
    <div class="container lg:!px-[120px]">
      <!-- Desktop: two columns -->
      <div class="hidden px-20 md:px-0 lg:grid lg:grid-cols-[38%_1fr] lg:gap-40 xl:gap-56">
        <div class="flex flex-col lg:my-32 lg:max-w-[250px]">
          <BaseHeader
            v-if="headingHtml"
            as="h2"
            :unwrap-outer-paragraph="true"
            :content="headingHtml"
            :objects="headingObjectsList"
            :content-class="headingContentClass"
          />
          <div
            v-if="ctaHref"
            class="mt-28"
          >
            <a
              :href="ctaHref"
              :target="ctaTarget || '_self'"
              class="bg-off-primary-500 inline-flex items-center justify-center rounded-full px-32 py-14 text-15 font-semibold text-white transition-opacity hover:opacity-90"
              :style="ctaStyle"
            >
              {{ ctaText }}
            </a>
          </div>
        </div>

        <div
          v-if="faqItems.length"
          class="flex flex-col"
        >
          <div
            v-for="(item, index) in faqItems"
            :key="`faq-d-${index}`"
            class="border-b-2 border-[#E2E2E2]"
          >
            <button
              type="button"
              class="flex w-full items-center justify-between gap-12 py-20 text-left md:py-24"
              :aria-expanded="openIndex === index"
              @click="toggle(index)"
            >
              <span class="text-off-primary-500 font-inter text-20 font-medium leading-snug">
                {{ item.question }}
              </span>
              <svg
                class="text-off-primary-500 size-24 shrink-0 transition-transform duration-200"
                :class="{ 'rotate-180': openIndex === index }"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <div
              class="faq-answer grid"
              :class="openIndex === index ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
            >
              <div class="overflow-hidden">
                <div
                  class="faq-answer-inner text-off-primary-500 pb-20 pr-32 text-15 leading-relaxed [&_a]:underline [&_ol]:mb-[0.5em] [&_ol]:list-decimal [&_ol]:pl-20 [&_p:last-child]:mb-0 [&_p]:mb-[0.65em] [&_ul]:mb-[0.5em] [&_ul]:list-disc [&_ul]:pl-20"
                  v-html="faqAnswerHtml(item.answer)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile: stacked -->
      <div class="px-20 md:px-24 lg:hidden">
        <BaseHeader
          v-if="headingHtml"
          as="h2"
          :unwrap-outer-paragraph="true"
          :content="headingHtml"
          :objects="headingObjectsList"
          :content-class="headingContentClass"
        />

        <div
          v-if="faqItems.length"
          class="mt-20 flex flex-col md:mt-24"
        >
          <div
            v-for="(item, index) in faqItems"
            :key="`faq-m-${index}`"
            class="border-b-2 border-[#E2E2E2]"
          >
            <button
              type="button"
              class="flex w-full items-center justify-between gap-20 py-16 text-left md:py-24"
              :aria-expanded="openIndex === index"
              @click="toggle(index)"
            >
              <span class="text-off-primary-500 font-inter text-[15.5px] font-medium leading-snug">
                {{ item.question }}
              </span>
              <svg
                class="text-off-primary-500 size-22 shrink-0 transition-transform duration-200"
                :class="{ 'rotate-180': openIndex === index }"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <div
              class="faq-answer grid"
              :class="openIndex === index ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
            >
              <div class="overflow-hidden">
                <div
                  class="faq-answer-inner text-off-primary-500 pb-16 pr-28 text-14 leading-relaxed md:pb-20 [&_a]:underline [&_ol]:mb-[0.5em] [&_ol]:list-decimal [&_ol]:pl-20 [&_p:last-child]:mb-0 [&_p]:mb-[0.65em] [&_ul]:mb-[0.5em] [&_ul]:list-disc [&_ul]:pl-20"
                  v-html="faqAnswerHtml(item.answer)"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="ctaHref"
          class="mt-24 md:mt-28"
        >
          <a
            :href="ctaHref"
            :target="ctaTarget || '_self'"
            class="bg-off-primary-500 flex w-full items-center justify-center rounded-full px-28 py-16 text-16 font-semibold text-white transition-opacity hover:opacity-90"
            :style="ctaStyle"
          >
            {{ ctaText }}
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.faq-answer {
  transition: grid-template-rows 0.3s ease;
}

.faq-answer-inner {
  tab-size: 4;
  word-wrap: break-word;
}
</style>
